/**
 * tutorStudentActivityAdapter — la fiche d'une activité vue par le tuteur, hors live : l'activité telle
 * que la sert `getActivities` (`with_logs`), ses vues et ses gestes.
 *
 * Le legacy est `student/activity/todo/TodoModal.js`, branche tuteur, en modale (`noModal` faux) et hors
 * live : ni « Afficher à l'élève » (il demande l'élève connecté à la salle), ni « Tableau blanc » (il
 * demande le centre du live). Le reste est la fiche du centre du live, même composant : les dérivations
 * déjà portées par `tutorLiveActivityAdapter` (le rendu vu du tuteur, les corps de « Soumettre » et de
 * « Redonner ») sont relues d'ici, pas recopiées.
 */
import { type UIActivityDetails, type UIActivityDisplayMode } from "../../../ui/types"
import { type UITutorActivityAction } from "../../../ui/types/tutorEleves"
import { toUIActivityDetails } from "../../parent/Activities/activityDetailsAdapter"
import { type ApiSchoolSubject } from "../../parent/shared/schoolSubjectAdapter"
import { toUITutorLiveWork, type ApiLiveActivity } from "../Live/tutorLiveActivityAdapter"

/** L'activité de la fiche : la même forme qu'au centre du live (`TodoModal` est un seul composant). */
export type ApiTutorActivity = ApiLiveActivity & {
    started_at?: string | null
    locked_at?: string | null
}

/** Au-delà de 800 px, la fiche montre l'énoncé et le rendu côte à côte (`TodoModal.canShowSplit`). */
export const TUTOR_ACTIVITY_SPLIT_MIN_WIDTH = 800

const hasWork = (raw: ApiTutorActivity | null | undefined) => (raw?.media_count ?? 0) > 0
const hasQuiz = (raw: ApiTutorActivity | null | undefined) => !!raw?.has_prerequisite_quiz

/**
 * Les vues offertes, dans l'ordre du legacy (`TodoModal:1230-1236`) : « Quizz » (un quiz de prérequis),
 * « Énoncé », « Rendu » (un rendu), « Énoncé + Rendu » (un rendu, au large). « Tableau blanc » n'existe
 * qu'au centre du live.
 */
export function tutorActivityDisplayModes(
    raw: ApiTutorActivity | null | undefined,
    wide: boolean
): UIActivityDisplayMode[] {
    const modes: UIActivityDisplayMode[] = []
    if (hasQuiz(raw)) modes.push("quiz")
    modes.push("material")
    if (hasWork(raw)) modes.push("work")
    if (hasWork(raw) && wide) modes.push("split")
    return modes
}

/** La vue d'ouverture (`TodoModal.getDefaultMode`) : le rendu, sinon le quiz, sinon l'énoncé. */
export function defaultTutorActivityDisplayMode(
    raw: ApiTutorActivity | null | undefined,
    wide: boolean
): UIActivityDisplayMode {
    if (hasWork(raw)) return wide ? "split" : "work"
    if (hasQuiz(raw)) return "quiz"
    return "material"
}

/**
 * Les gestes de la fiche, hors live, dans l'ordre de `renderHeaderActions` puis du menu
 * (`TodoModal:813-1137`) :
 *  - « Générer... » : pas depuis les archives, l'exercice a ses compétences clés, et l'écran sait générer ;
 *  - « Soumettre » : pas finie, un rendu envoyé — depuis les archives aussi, comme le legacy ;
 *  - « Valider » : rejetée ; « Redonner » : finie — depuis les archives aussi ;
 *  - le menu : « Restaurer » depuis les archives ; sinon « Archiver », et « Modifier le titre » pas finie.
 *
 * Le refus « Non autorisé » du legacy (`canEditActivity`) ne se pose pas : le tuteur a le droit
 * `assign_activities` (`RolesAndPermissionsSeeder`), qui l'autorise toujours.
 */
export function tutorActivityActions(
    raw: ApiTutorActivity | null | undefined,
    context: { fromArchived: boolean; canGenerate: boolean }
): UITutorActivityAction[] {
    if (raw?.id == null) return []
    const actions: UITutorActivityAction[] = []
    if (!context.fromArchived && context.canGenerate && raw.material?.material_key_skills) actions.push("generate")
    if (!raw.finished_at && hasWork(raw)) actions.push("submit")
    if (raw.status === "rejected") actions.push("validate")
    if (raw.finished_at) actions.push("redo")
    if (context.fromArchived) {
        actions.push("restore")
    } else {
        actions.push("archive")
        if (!raw.finished_at) actions.push("editTitle")
    }
    return actions
}

/**
 * L'activité, en `UIActivityDetails` : la fiche du parent pour l'en-tête et l'énoncé, le rendu vu du
 * tuteur (`toUITutorLiveWork` : les points de la correction se montrent au tuteur dès qu'ils existent).
 */
export function toUITutorActivityDetails(
    raw: ApiTutorActivity | null | undefined,
    options: { subject?: ApiSchoolSubject | null; cleanUrl: (url: string | null | undefined) => string | null }
): UIActivityDetails | null {
    const details = toUIActivityDetails(raw, options)
    if (details == null) return null
    return { ...details, work: toUITutorLiveWork(raw, options.cleanUrl) }
}

/** L'activité n'est plus dans les listes de l'élève (archivée ailleurs) : la fiche se ferme (`TodoModal:316-335`). */
export function activityStillListed(
    student: { todo_activities?: { id?: unknown }[] | null; activities?: { id?: unknown }[] | null } | null | undefined,
    activityId: string | number | null
): boolean {
    if (student == null || activityId == null) return true
    return [...(student.todo_activities ?? []), ...(student.activities ?? [])].some(
        (activity) => activity?.id != null && String(activity.id) === String(activityId)
    )
}

/**
 * Ce que « Archiver » répond, une fois l'activité relue (`getActivity`) : verrouillée par l'élève (il l'a
 * ouverte), elle ne s'archive pas (`TodoModal:384-391`) ; commencée par lui, non plus (la liste,
 * `TodoListScreen:391-401`) ; sinon la question.
 */
export function archiveAnswerFor(
    fresh: Pick<ApiTutorActivity, "locked_at" | "started_at" | "student_id"> | null | undefined,
    meId: unknown
): "lockedByStudent" | "startedRecently" | "archive" {
    const byOther = fresh?.student_id == null || String(fresh.student_id) !== String(meId)
    if (fresh?.locked_at && byOther) return "lockedByStudent"
    if (fresh?.started_at && byOther) return "startedRecently"
    return "archive"
}
